import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/announcements';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = {
  title: '',
  description: '',
  imageUrl: '',
  effectiveStartDate: '',
  effectiveEndDate: '',
  isActive: 'true',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function toInputDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}
function toPayloadDate(value) {
  return value ? new Date(value).toISOString() : null;
}

function rowToForm(row) {
  return {
    title: pick(row, 'title', 'Title') || '',
    description: pick(row, 'description', 'Description') || '',
    imageUrl: pick(row, 'imageUrl', 'ImageUrl') || '',
    effectiveStartDate: toInputDate(pick(row, 'effectiveStartDate', 'EffectiveStartDate')),
    effectiveEndDate: toInputDate(pick(row, 'effectiveEndDate', 'EffectiveEndDate')),
    isActive: (pick(row, 'isActive', 'IsActive') ?? false) !== false ? 'true' : 'false',
  };
}

export function AnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Duyuru' : 'Duyuru Detayı', 'Duyuru tanımı, tarih aralığı ve görseli.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    // 1) navigation state'ten gelen satır; 2) GetById; 3) listeyi çekip id ile bul
    if (location.state?.row) {
      const row = location.state.row;
      setForm(rowToForm(row));
      // Nav-state satırı (liste $select) audit alanlarını içermez → tam kaydı arka planda
      // (bloklamadan) çekip audit'i doldur.
      fetch(`${API_BASE}/GetById?id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = unwrap(body);
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/GetById?id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (res.ok) {
        const r = unwrap(await res.json());
        setForm(rowToForm(r));
        setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        return;
      }
      // GetById başarısızsa listeden bul
      const listRes = await fetch(`${API_BASE}/Select`, { headers: authHeaders() });
      if (!listRes.ok) throw new Error(`Duyuru alınamadı (${res.status})`);
      const items = unwrap(await listRes.json());
      const match = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!match) throw new Error('Duyuru bulunamadı');
      setForm(rowToForm(match));
      setAudit({ createdBy: pick(match, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(match, 'createdDate', 'CreatedDate'), updatedBy: pick(match, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(match, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Duyuru alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /announcements/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set('imageUrl', reader.result);
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    if (!form.title.trim()) return 'Duyuru başlığı boş olamaz';
    if (!form.effectiveStartDate || !form.effectiveEndDate) return 'Geçerlilik tarih aralığı zorunludur';
    if (new Date(form.effectiveEndDate) < new Date(form.effectiveStartDate)) return 'Bitiş tarihi başlangıçtan önce olamaz';
    return null;
  };

  const buildBody = () => ({
    title: form.title,
    description: form.description,
    imageUrl: form.imageUrl,
    effectiveStartDate: toPayloadDate(form.effectiveStartDate),
    effectiveEndDate: toPayloadDate(form.effectiveEndDate),
    isActive: form.isActive === 'true',
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    const validation = validateForm();
    if (validation) { showToast('error', validation); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(),
        body: JSON.stringify(isNew ? buildBody() : { id, ...buildBody() }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || (isNew ? 'Duyuru eklenemedi' : 'Duyuru güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Duyuru eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Duyuru eklendi.');
        setTimeout(() => navigate('/announcements'), 400);
        return;
      }
      showToast('success', 'Duyuru güncellendi.');
      setTimeout(() => navigate('/announcements'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/announcements/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Duyuru silinemedi');
      showToast('success', 'Duyuru silindi.');
      setTimeout(() => navigate('/announcements'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Duyuru' : (form.title || 'Duyuru Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/announcements')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni duyuru aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Duyurular', to: '/announcements' }, { label: isNew ? 'Yeni Duyuru' : 'Duyuru Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Announcement', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="info" title="Duyuru Bilgileri" subtitle="Başlık, geçerlilik aralığı ve durum">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="title" label="Tanım" req span placeholder="Duyuru başlığı giriniz" />
            <FieldText form={form} set={set} tried={tried} k="effectiveStartDate" label="Başlangıç" req type="datetime-local" />
            <FieldText form={form} set={set} tried={tried} k="effectiveEndDate" label="Bitiş" req type="datetime-local" />
            <FieldSelect form={form} set={set} k="isActive" label="Aktif" options={ACTIVE_OPTIONS} />
          </Grid>
        </Section>

        <Section icon="layers" title="Görsel" subtitle="Duyuru görseli (URL veya dosya)">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="imageUrl" label="Görsel URL" span placeholder="https://... veya dosya seçimi" />
            <FieldCustom label="Görsel Dosyası" span>
              <input type="file" accept="image/*" onChange={handleImageFile} />
            </FieldCustom>
          </Grid>
          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt={form.title || 'Duyuru görseli'}
              style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', marginTop: 12 }}
            />
          )}
        </Section>

        <Section icon="list" title="Açıklama" subtitle="Duyuru metni">
          <Grid cols={1}>
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={5} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title}”</strong> duyurusunu silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AnnouncementDetailPage;
